import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence, Reorder } from "framer-motion";
import { ArrowLeft, Upload, Trash2, RotateCw, RotateCcw, Type, GripVertical, ChevronLeft, ChevronRight, X, ZoomIn, Save, ImageIcon, Droplets, Maximize2, AlertTriangle, RefreshCw, ShoppingBag, Sparkles, Instagram, Wand2, Users, Sun, CheckCircle2, XCircle, Loader2, Film, Music, Download, Star, ArrowUpDown, Plus, Tag, FileText, Share2 } from "lucide-react";
import SocialShareDialog from "@/components/SocialShareDialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { errorMessage } from "@/lib/errors";
import { useUploadQueue } from "@/contexts/UploadQueueContext";

export default function AdminGalleryManager() {
  const { galleryId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [gallery, setGallery] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionText, setCaptionText] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [watermarkPanel, setWatermarkPanel] = useState(null);
  const [wmPos, setWmPos] = useState("bottom-center");
  const [wmOpacity, setWmOpacity] = useState(150);
  const [wmSize, setWmSize] = useState(100);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const replaceRef = useRef(null);
  const [smartReprocess, setSmartReprocess] = useState(null); // { id } while running, full result when done
  const [bulkReprocess, setBulkReprocess] = useState(null); // { items: [...], cancel: bool, done: bool }
  const [highlightReel, setHighlightReel] = useState(null); // { open, generating, result, musicTracks, selectedMusicId, subtitle }
  const [movePanel, setMovePanel] = useState(null); // { id, current_position } when "move to position" popover is open
  const [moveInput, setMoveInput] = useState("");
  const [shareDialog, setShareDialog] = useState(null); // { id, url } when social-share modal is open
  const uploadQueue = useUploadQueue();

  useEffect(() => {
    loadGallery();
  }, [galleryId]);

  // Derived from the global queue — "is something uploading for this gallery
  // right now?" Used to dim the empty-state CTA when uploads are pending.
  const localUploading = uploadQueue.tasks.some(
    (t) => t.galleryId === galleryId
      && (t.status === uploadQueue.STATUS.QUEUED || t.status === uploadQueue.STATUS.RUNNING),
  );

  // Subscribe to background upload completions targeting this gallery.
  // When a queued upload finishes (the user may have navigated away
  // and come back), we silently reload the grid so the new media
  // appears without a manual refresh.
  useEffect(() => {
    if (!galleryId) return undefined;
    const unsub = uploadQueue.subscribe(galleryId, (event) => {
      if (event.type === "upload-complete") {
        loadGallery();
        toast.success(
          event.batch
            ? `${event.batch.uploaded ?? "Some"} photo(s) uploaded.`
            : `${event.task.filename} uploaded.`,
        );
      } else if (event.type === "upload-failed") {
        toast.error(`Upload failed: ${event.task.filename} — ${event.error}`);
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryId, uploadQueue]);

  const loadGallery = async () => {
    try {
      const res = await api.get(`/admin/galleries/${galleryId}/media`);
      setGallery(res.data.gallery);
      setMedia(res.data.media || []);
    } catch { toast.error("Failed to load gallery."); }
    finally { setLoading(false); }
  };

  const handleMultiUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    // Drop into the global background queue and return immediately.
    // The UploadQueueTray (bottom-right) shows live progress; the
    // editor is free to navigate away, edit other galleries, etc.
    const ids = uploadQueue.enqueue(galleryId, files, {
      galleryTitle: gallery?.title || "",
    });
    if (ids.length > 0) {
      toast.success(
        files.length === 1
          ? `Queued ${files[0].name} — track progress in the tray.`
          : `Queued ${files.length} file(s) — track progress in the tray.`,
      );
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const deleteMedia = async (id) => {
    try {
      await api.delete(`/admin/gallery-media/${id}`);
      setMedia(prev => prev.filter(m => m.id !== id));
      setDeleteConfirm(null);
      toast.success("Photo deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    // Use the batch endpoint so editor's 5+ deletes route through the
    // approval queue in one shot. Single deletes still hit
    // /admin/gallery-media/{id} (not gated).
    if (ids.length >= 5) {
      try {
        const res = await api.post("/admin/gallery-media/bulk-delete", { ids });
        if (res.status === 202 && res.data?.pending_approval) {
          toast.success(res.data.message || "Bulk delete queued for admin approval.");
          setSelected(new Set());
          return;
        }
        // Admin path — backend executed immediately.
        setMedia((prev) => prev.filter((m) => !selected.has(m.id)));
        toast.success(`${res.data?.deleted ?? ids.length} photo(s) deleted.`);
      } catch {
        toast.error("Bulk delete failed.");
      }
      setSelected(new Set());
      return;
    }
    // Small batches — individual deletes (existing fast path).
    for (const id of ids) {
      await api.delete(`/admin/gallery-media/${id}`).catch(() => {});
    }
    setMedia((prev) => prev.filter((m) => !selected.has(m.id)));
    toast.success(`${ids.length} photo(s) deleted.`);
    setSelected(new Set());
  };

  const rotateMedia = async (id, degrees) => {
    setSaving(true);
    try {
      await api.post(`/admin/gallery-media/${id}/rotate`, { degrees });
      toast.success(`Rotated ${degrees > 0 ? "right" : "left"}.`);
      loadGallery();
    } catch { toast.error("Rotate failed."); }
    finally { setSaving(false); }
  };

  const saveCaption = async (id) => {
    try {
      await api.patch(`/admin/gallery-media/${id}`, { caption: captionText });
      setMedia(prev => prev.map(m => m.id === id ? { ...m, caption: captionText } : m));
      setEditingCaption(null);
      toast.success("Caption saved.");
    } catch { toast.error("Failed to save caption."); }
  };

  const saveReorder = async (newOrder) => {
    setMedia(newOrder);
    const order = newOrder.map(m => m.id);
    try {
      await api.post(`/admin/galleries/${galleryId}/reorder`, { order });
    } catch { toast.error("Reorder failed."); }
  };

  // Click-to-jump: move a single photo to an arbitrary position.
  // 1-indexed input ("first" = 1, "last" = media.length).
  const moveToPosition = async (mediaId, targetPosition1) => {
    const target = Math.max(1, Math.min(media.length, parseInt(targetPosition1) || 1));
    const fromIdx = media.findIndex(m => m.id === mediaId);
    if (fromIdx === -1) return;
    const newOrder = [...media];
    const [item] = newOrder.splice(fromIdx, 1);
    newOrder.splice(target - 1, 0, item);
    await saveReorder(newOrder);
    toast.success(`Moved to position ${target}`);
    setMovePanel(null);
    setMoveInput("");
  };

  const applyWatermark = async () => {
    if (!watermarkPanel) return;
    setSaving(true);
    try {
      await api.post(`/admin/gallery-media/${watermarkPanel}/rewatermark`, { position: wmPos, opacity: wmOpacity, size: wmSize });
      setMedia(prev => prev.map(m => m.id === watermarkPanel ? { ...m, watermark_position: wmPos, watermark_opacity: wmOpacity, watermark_size: wmSize } : m));
      toast.success("Watermark updated.");
      setWatermarkPanel(null);
      loadGallery();
    } catch { toast.error("Watermark update failed."); }
    finally { setSaving(false); }
  };

  const [lastClickedIndex, setLastClickedIndex] = useState(null);

  const toggleSelect = (id, evt) => {
    setSelected(prev => {
      const next = new Set(prev);
      // Shift-click → range select from the last clicked index to this one.
      const thisIndex = media.findIndex(m => m.id === id);
      if (evt?.shiftKey && lastClickedIndex !== null && thisIndex !== -1) {
        const [lo, hi] = thisIndex < lastClickedIndex
          ? [thisIndex, lastClickedIndex]
          : [lastClickedIndex, thisIndex];
        for (let i = lo; i <= hi; i++) {
          if (media[i]) next.add(media[i].id);
        }
      } else {
        if (next.has(id)) next.delete(id); else next.add(id);
      }
      if (thisIndex !== -1) setLastClickedIndex(thisIndex);
      return next;
    });
  };

  const selectAllVisible = () => {
    // "Visible" matches whatever the current Reorder.Group renders.
    // Right now `media` IS the visible array; future-proofs for filters.
    setSelected(new Set(media.map(m => m.id)));
    toast.success(`Selected all ${media.length} photos.`);
  };

  const deselectAll = () => setSelected(new Set());

  // ============== BULK ACTIONS ==============
  // All wired to the new /admin/gallery-media/bulk-* endpoints. Each
  // generates a session_id so the activity_log groups the batch as one
  // entry. After a successful action we either refresh the gallery
  // (rotate/watermark/caption) or just clear the selection.
  const _bulkSession = () => `bulk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const bulkRotate = async (degrees) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/gallery-media/bulk-rotate", { ids, degrees, session_id: _bulkSession() });
      toast.success(`Rotated ${r.data.rotated} photos · ${degrees}°`);
      await loadGallery();
      setSelected(new Set());
    } catch { toast.error("Bulk rotate failed."); }
    finally { setSaving(false); }
  };

  const bulkWatermark = async (position = "bottom-center", opacity = 150, size = 100) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/gallery-media/bulk-watermark", { ids, position, opacity, size, session_id: _bulkSession() });
      toast.success(`Watermark applied to ${r.data.updated} photos.`);
      await loadGallery();
      setSelected(new Set());
    } catch { toast.error("Bulk watermark failed."); }
    finally { setSaving(false); }
  };

  const bulkCaption = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const caption = window.prompt(`Set the same caption on ${ids.length} photos. Leave blank to clear.`, "");
    if (caption === null) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/gallery-media/bulk-caption", { ids, caption, session_id: _bulkSession() });
      toast.success(`Caption updated on ${r.data.updated} photos.`);
      await loadGallery();
      setSelected(new Set());
    } catch { toast.error("Bulk caption failed."); }
    finally { setSaving(false); }
  };

  const bulkTag = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const raw = window.prompt(`Add tags to ${ids.length} photos. Comma-separated.`, "");
    if (!raw) return;
    const tags = raw.split(",").map(t => t.trim()).filter(Boolean);
    setSaving(true);
    try {
      const r = await api.post("/admin/gallery-media/bulk-tag", { ids, tags, mode: "add", session_id: _bulkSession() });
      toast.success(`Tagged ${r.data.updated} photos with ${tags.length} tag(s).`);
      setSelected(new Set());
    } catch { toast.error("Bulk tag failed."); }
    finally { setSaving(false); }
  };

  const bulkExport = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/gallery-media/bulk-export", { ids, session_id: _bulkSession() });
      const items = r.data.items || [];
      // Build a manifest text file the editor can use; for a real ZIP
      // we'd use JSZip here, but a manifest opens-each-link approach
      // works without adding a heavy library.
      const lines = items.map(i => `${i.filename || i.id}: ${i.url}`);
      const blob = new Blob([lines.join("\n")], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `export-manifest-${Date.now()}.txt`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${items.length} photo URLs to manifest.`);
    } catch { toast.error("Bulk export failed."); }
    finally { setSaving(false); }
  };

  // ============== FAST ALBUM CREATION ==============
  const [createAlbum, setCreateAlbum] = useState({ open: false, title: "", event_type: "Portfolio", event_date: "", description: "", action: "copy", draft: false });

  const submitCreateAlbum = async () => {
    if (!createAlbum.title.trim()) { toast.error("Album title is required."); return; }
    const ids = Array.from(selected);
    if (!ids.length) return;
    setSaving(true);
    try {
      const r = await api.post("/admin/galleries/from-selection", {
        source_ids: ids,
        title: createAlbum.title.trim(),
        event_type: createAlbum.event_type,
        event_date: createAlbum.event_date || null,
        description: createAlbum.description || null,
        action: createAlbum.action,
        draft: createAlbum.draft,
      });
      toast.success(`Created "${r.data.gallery.title}" with ${r.data.media_count} photos.`);
      setCreateAlbum({ open: false, title: "", event_type: "Portfolio", event_date: "", description: "", action: "copy", draft: false });
      setSelected(new Set());
      if (createAlbum.action === "move") await loadGallery();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create album.");
    } finally { setSaving(false); }
  };

  const handleReplace = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceTarget) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/admin/gallery-media/${replaceTarget}/replace`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia(prev => prev.map(m => m.id === replaceTarget ? { ...m, url: res.data.url, filename: res.data.filename } : m));
      toast.success("Photo replaced and enhanced.");
      setReplaceTarget(null);
      loadGallery();
    } catch { toast.error("Replace failed."); }
    finally { setSaving(false); if (replaceRef.current) replaceRef.current.value = ""; }
  };

  const toggleLicensing = async (id, current) => {
    try {
      await api.patch(`/admin/gallery-media/${id}/licensing`, { licensable: !current });
      setMedia(prev => prev.map(m => m.id === id ? { ...m, licensable: !current } : m));
      toast.success(!current ? "Listed on marketplace." : "Removed from marketplace.");
    } catch { toast.error("Failed to update licensing."); }
  };

  const aiCaption = async (id) => {
    setSaving(true);
    try {
      const res = await api.post(`/admin/gallery-media/${id}/ai-caption`);
      setMedia(prev => prev.map(m => m.id === id ? { ...m, caption: res.data.caption } : m));
      toast.success("AI caption generated.");
    } catch { toast.error("AI caption failed."); }
    finally { setSaving(false); }
  };

  const aiCaptionAll = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/admin/galleries/${galleryId}/ai-caption-all`);
      toast.success(res.data.message);
      loadGallery();
    } catch { toast.error("AI captioning failed."); }
    finally { setSaving(false); }
  };

  const generateIGPost = async (mediaId) => {
    setSaving(true);
    try {
      const res = await api.post(`/admin/instagram/generate-post/${mediaId}`);
      toast.success("Instagram post ready! Check the Instagram tab in Admin Dashboard.");
    } catch { toast.error("IG post generation failed."); }
    finally { setSaving(false); }
  };

  const reprocessSmart = async (mediaId) => {
    setSmartReprocess({ id: mediaId, loading: true });
    try {
      const res = await api.post(`/admin/gallery-media/${mediaId}/reprocess-smart`);
      setSmartReprocess({ ...res.data, loading: false });
      // Refresh the in-list URL with cache-bust so the grid thumbnail updates too
      setMedia(prev => prev.map(m => m.id === mediaId ? { ...m, url: `${m.url.split("?")[0]}?v=${Date.now()}` } : m));
      toast.success("Smart pipeline applied. Compare before / after.");
    } catch (err) {
      setSmartReprocess(null);
      toast.error(errorMessage(err, "Smart reprocess failed."));
    }
  };

  const reprocessSelected = async () => {
    if (!selected.size) return;
    const ids = Array.from(selected);
    const items = ids.map(id => {
      const m = media.find(x => x.id === id);
      return { id, filename: m?.filename || "photo", thumb: m?.url, status: "pending", result: null, error: null };
    });
    // The cancel flag is read by the worker loop on each iteration via the latest state.
    const stateRef = { items, cancel: false, done: false };
    setBulkReprocess(stateRef);

    const concurrency = 2;
    let cursor = 0;
    const updateItem = (idx, patch) => {
      stateRef.items[idx] = { ...stateRef.items[idx], ...patch };
      setBulkReprocess({ ...stateRef, items: [...stateRef.items] });
    };

    const worker = async () => {
      while (true) {
        if (stateRef.cancel) return;
        const idx = cursor++;
        if (idx >= ids.length) return;
        updateItem(idx, { status: "processing" });
        try {
          const res = await api.post(`/admin/gallery-media/${ids[idx]}/reprocess-smart`);
          updateItem(idx, { status: "done", result: res.data });
          // Refresh thumbnail in the grid
          setMedia(prev => prev.map(m => m.id === ids[idx] ? { ...m, url: `${m.url.split("?")[0]}?v=${Date.now()}` } : m));
        } catch (err) {
          updateItem(idx, { status: "error", error: errorMessage(err, "Failed") });
        }
      }
    };

    await Promise.all(Array.from({ length: concurrency }, worker));
    stateRef.done = true;
    setBulkReprocess({ ...stateRef, items: [...stateRef.items] });
    const okCount = stateRef.items.filter(i => i.status === "done").length;
    const errCount = stateRef.items.filter(i => i.status === "error").length;
    if (errCount === 0) {
      toast.success(`Smart-reprocessed ${okCount} photo(s).`);
    } else {
      toast.warning(`Done: ${okCount} succeeded, ${errCount} failed.`);
    }
  };

  const openHighlightReelDialog = async () => {
    setHighlightReel({ open: true, generating: false, scoring: false, autoScore: false, result: null, musicTracks: [], selectedMusicId: "", subtitle: gallery?.event_type || "" });
    try {
      const res = await api.get("/admin/highlight-reels/music");
      setHighlightReel(prev => ({ ...prev, musicTracks: res.data, selectedMusicId: res.data[0]?.id || "" }));
    } catch { toast.error("Failed to load music library."); }
  };

  const generateHighlightReel = async () => {
    if (!highlightReel) return;

    // Path A — "Score & Auto-Reel": fire score-gallery with auto_reel=true,
    // then poll the reels list until a new one shows up.
    if (highlightReel.autoScore) {
      setHighlightReel(prev => ({ ...prev, scoring: true, generating: true, result: null }));
      try {
        // Snapshot existing reels so we can detect the new one.
        const before = await api.get(`/admin/highlight-reels?gallery_id=${galleryId}`);
        const existingIds = new Set((before.data || []).map(r => r.id));

        const scoreRes = await api.post(`/admin/curation/score-gallery/${galleryId}`, {
          auto_reel: true,
          music_id: highlightReel.selectedMusicId,
          subtitle: highlightReel.subtitle || "",
        });
        toast.success(scoreRes.data.message || "Scoring complete. Reel rendering...");
        setHighlightReel(prev => ({ ...prev, scoring: false }));

        // Poll for the new reel — try every 3s for up to 90s.
        let foundReel = null;
        for (let i = 0; i < 30; i++) {
          await new Promise(r => setTimeout(r, 3000));
          try {
            const list = await api.get(`/admin/highlight-reels?gallery_id=${galleryId}`);
            foundReel = (list.data || []).find(r => !existingIds.has(r.id));
            if (foundReel) break;
          } catch { /* ignore transient errors */ }
        }
        if (foundReel) {
          setHighlightReel(prev => ({ ...prev, generating: false, result: foundReel }));
          toast.success("Highlight reel ready.");
          // Refresh media in the grid so newly-scored thumbnails show updated AI scores
          loadGallery();
        } else {
          setHighlightReel(prev => ({ ...prev, generating: false }));
          toast.error("Reel didn't finish in 90s. Check the Highlight Reels list manually.");
        }
      } catch (err) {
        setHighlightReel(prev => ({ ...prev, scoring: false, generating: false }));
        toast.error(errorMessage(err, "Score & Reel failed."));
      }
      return;
    }

    // Path B — direct reel generation (no scoring step).
    setHighlightReel(prev => ({ ...prev, generating: true, result: null }));
    try {
      const res = await api.post(`/admin/galleries/${galleryId}/highlight-reel`, {
        music_id: highlightReel.selectedMusicId,
        subtitle: highlightReel.subtitle || "",
      }, { timeout: 240000 });
      setHighlightReel(prev => ({ ...prev, generating: false, result: res.data }));
      toast.success("Highlight reel ready.");
    } catch (err) {
      setHighlightReel(prev => ({ ...prev, generating: false }));
      toast.error(errorMessage(err, "Reel generation failed."));
    }
  };

  if (loading) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><div className="text-gold font-body animate-pulse">Loading Gallery...</div></div>;

  const images = media.filter(m => m.media_type === "image");

  return (
    <div data-testid="admin-gallery-manager" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/admin/dashboard")} className="text-white/40 hover:text-gold transition-colors" data-testid="back-to-dashboard"><ArrowLeft size={20} /></button>
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Gallery Manager</p>
              <h1 className="font-heading text-2xl text-white">{gallery?.title || "Gallery"}</h1>
              <p className="text-white/30 text-xs font-body mt-0.5">{gallery?.client_email} — {media.length} photos</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Quick select-all/deselect controls — always visible so the
                editor can scope a batch with one click. */}
            <div className="flex items-center gap-1">
              <button onClick={selectAllVisible} data-testid="select-all-visible" className="text-[10px] font-body uppercase tracking-wider text-white/40 hover:text-gold border border-white/10 hover:border-gold/40 px-2 py-1.5 transition-colors">
                Select all ({media.length})
              </button>
              {selected.size > 0 && (
                <button onClick={deselectAll} data-testid="deselect-all" className="text-[10px] font-body uppercase tracking-wider text-white/40 hover:text-white border border-white/10 hover:border-white/30 px-2 py-1.5 transition-colors">
                  Clear
                </button>
              )}
            </div>
            <div className="flex border border-white/10">
              <button onClick={() => setViewMode("grid")} className={`px-3 py-2 text-xs font-body transition-colors ${viewMode === "grid" ? "bg-gold/15 text-gold" : "text-white/40 hover:text-white"}`} data-testid="view-grid">Grid</button>
              <button onClick={() => setViewMode("carousel")} className={`px-3 py-2 text-xs font-body transition-colors ${viewMode === "carousel" ? "bg-gold/15 text-gold" : "text-white/40 hover:text-white"}`} data-testid="view-carousel">Carousel</button>
            </div>
            <button onClick={async () => { setSaving(true); toast.info("AI HD-reprocessing all images..."); try { const res = await api.post(`/admin/galleries/${galleryId}/reprocess-media`); toast.success(res.data.message); loadGallery(); } catch { toast.error("Reprocess failed"); } finally { setSaving(false); }}} disabled={saving} className="flex items-center gap-2 bg-purple-500/15 text-purple-300 px-4 py-2 text-xs font-body hover:bg-purple-500/25 transition-colors disabled:opacity-40 border border-purple-500/30" data-testid="reprocess-gallery-btn">
              <Sparkles size={14} /> AI HD Reprocess
            </button>
            <button onClick={aiCaptionAll} disabled={saving} className="flex items-center gap-2 bg-white/5 text-white/50 px-4 py-2 text-xs font-body hover:bg-gold/15 hover:text-gold transition-colors disabled:opacity-40" data-testid="ai-caption-all-btn">
              <Sparkles size={14} /> AI Caption All
            </button>
            <button onClick={openHighlightReelDialog} disabled={saving || images.length < 2} className="flex items-center gap-2 bg-gradient-to-r from-gold/30 to-gold/15 text-gold px-4 py-2 text-xs font-body hover:from-gold/40 hover:to-gold/25 transition-all disabled:opacity-40 border border-gold/30" data-testid="highlight-reel-btn" title="Generate a 30-second cinematic Reel from top-scored photos">
              <Film size={14} /> AI Highlight Reel
            </button>
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 bg-gold/15 text-gold px-5 py-2 text-xs font-body hover:bg-gold/25 transition-colors" data-testid="upload-photos-btn">
              <Upload size={14} /> Upload Photos
            </button>
            <input ref={fileRef} type="file" multiple accept="image/*,video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v" onChange={handleMultiUpload} className="hidden" data-testid="multi-file-input" />
            <input ref={replaceRef} type="file" accept="image/*" onChange={handleReplace} className="hidden" data-testid="replace-file-input" />
          </div>
        </div>

        {/* Inline progress bar removed — the bottom-right Upload Queue
            Tray (mounted at the App root) shows live progress for every
            queued / running / completed / failed upload. The editor can
            navigate away from this gallery and the tray follows them. */}

        {/* Grid View */}
        {viewMode === "grid" && (
          <Reorder.Group axis="y" values={media} onReorder={saveReorder} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="gallery-grid">
            {media.map((m) => (
              <Reorder.Item key={m.id} value={m} className="relative group" data-testid={`media-item-${m.id}`}>
                <div className="aspect-square bg-white/5 overflow-hidden relative">
                  {m.media_type === "image" ? (
                    <img src={m.url} alt={m.caption || m.filename} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20"><ImageIcon size={32} /></div>
                  )}
                  {/* Selection checkbox */}
                  <button onClick={(e) => toggleSelect(m.id, e)} className={`absolute top-2 left-2 w-6 h-6 border flex items-center justify-center text-[10px] transition-all ${selected.has(m.id) ? "bg-gold border-gold text-black" : "border-white/40 bg-black/60 text-white/0 hover:text-white/80 hover:border-white/60"}`} data-testid={`select-${m.id}`} title="Click to select. Shift-click for range.">
                    {selected.has(m.id) && "✓"}
                  </button>
                  {/* Always-visible action bar (works on touch, no hover needed).
                      Sits at the bottom of the image with a translucent gradient
                      backdrop so the photo is still readable underneath. */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/55 to-transparent pt-6 pb-1.5 px-1.5 flex items-center justify-center gap-1 flex-wrap" data-testid={`actions-${m.id}`}>
                    <button onClick={() => setLightbox(m)} data-testid={`view-${m.id}`} className="p-1.5 bg-black/60 hover:bg-gold/40 rounded transition-colors" title="View"><ZoomIn size={13} className="text-white" /></button>
                    <button onClick={() => rotateMedia(m.id, -90)} data-testid={`rotate-left-${m.id}`} className="p-1.5 bg-black/60 hover:bg-gold/40 rounded transition-colors" title="Rotate Left"><RotateCcw size={13} className="text-white" /></button>
                    <button onClick={() => rotateMedia(m.id, 90)} data-testid={`rotate-right-${m.id}`} className="p-1.5 bg-black/60 hover:bg-gold/40 rounded transition-colors" title="Rotate Right"><RotateCw size={13} className="text-white" /></button>
                    <button onClick={() => { setReplaceTarget(m.id); replaceRef.current?.click(); }} data-testid={`replace-${m.id}`} className="p-1.5 bg-black/60 hover:bg-gold/40 rounded transition-colors" title="Replace Photo"><RefreshCw size={13} className="text-white" /></button>
                    <button onClick={() => reprocessSmart(m.id)} disabled={smartReprocess?.loading} className="p-1.5 bg-black/60 hover:bg-gold/50 rounded transition-colors disabled:opacity-30" title="Smart Reprocess (Auto-Orient + Face-Aware Watermark)" data-testid={`smart-reprocess-${m.id}`}><Wand2 size={13} className="text-gold" /></button>
                    <button onClick={() => generateIGPost(m.id)} disabled={saving} data-testid={`ig-post-${m.id}`} className="p-1.5 bg-black/60 hover:bg-pink-500/40 rounded transition-colors disabled:opacity-30" title="Create Instagram Post (1080×1080)"><Instagram size={13} className="text-white" /></button>
                    <button onClick={() => setShareDialog(m)} data-testid={`share-all-${m.id}`} className="p-1.5 bg-black/60 hover:bg-gold/40 rounded transition-colors" title="Share to all 5 platforms (IG, TikTok, X, LinkedIn, FB)"><Share2 size={13} className="text-gold" /></button>
                    <button onClick={() => { setWatermarkPanel(m.id); setWmPos(m.watermark_position || "bottom-center"); setWmOpacity(m.watermark_opacity || 150); setWmSize(m.watermark_size || 100); }} data-testid={`watermark-${m.id}`} className="p-1.5 bg-black/60 hover:bg-gold/40 rounded transition-colors" title="Watermark"><Droplets size={13} className="text-white" /></button>
                    <button onClick={() => { const idx = media.findIndex(x => x.id === m.id); setMovePanel({ id: m.id, current_position: idx + 1 }); setMoveInput(String(idx + 1)); }} data-testid={`move-${m.id}`} className="p-1.5 bg-black/60 hover:bg-gold/40 rounded transition-colors" title="Move to position"><ArrowUpDown size={13} className="text-white" /></button>
                    <button onClick={() => setDeleteConfirm(m.id)} data-testid={`delete-${m.id}`} className="p-1.5 bg-red-500/30 hover:bg-red-500/60 rounded transition-colors" title="Delete"><Trash2 size={13} className="text-red-300" /></button>
                  </div>
                  {/* Position number badge — bottom-left, always visible so admin sees current ordering */}
                  <div data-testid={`position-badge-${m.id}`} className="absolute top-2 right-2 bg-luxury-black/80 backdrop-blur text-gold border border-gold/30 px-1.5 py-0.5 text-[10px] font-body font-medium">
                    #{media.findIndex(x => x.id === m.id) + 1}
                  </div>
                  {/* Drag handle — always visible top-right (just under the position badge on the same axis) */}
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 text-white/30 cursor-grab"><GripVertical size={14} /></div>
                </div>
                {/* Caption + Actions area */}
                <div className="p-2 bg-luxury-charcoal border-t border-white/5">
                  <div className="flex items-center gap-1 mb-1">
                    <button onClick={() => toggleLicensing(m.id, m.licensable)} className={`text-[9px] font-body px-1.5 py-0.5 transition-colors ${m.licensable ? "bg-green-500/15 text-green-400" : "bg-white/5 text-white/20 hover:text-white/40"}`} data-testid={`license-toggle-${m.id}`} title={m.licensable ? "Listed on marketplace" : "Not listed"}>
                      <ShoppingBag size={9} className="inline mr-0.5" />{m.licensable ? "Listed" : "Unlisted"}
                    </button>
                    <button onClick={() => aiCaption(m.id)} disabled={saving} className="text-[9px] font-body px-1.5 py-0.5 bg-white/5 text-white/20 hover:text-gold/60 transition-colors disabled:opacity-30" data-testid={`ai-caption-btn-${m.id}`} title="Generate AI Caption">
                      <Sparkles size={9} className="inline mr-0.5" />AI
                    </button>
                    {m.ai_score && <span className="text-[9px] font-body text-gold/40 ml-auto">{Math.round(m.ai_score)}pts</span>}
                  </div>
                  {editingCaption === m.id ? (
                    <div className="flex gap-1">
                      <input value={captionText} onChange={e => setCaptionText(e.target.value)} className="flex-1 bg-white/5 border border-white/10 text-white text-[10px] px-2 py-1 font-body focus:outline-none focus:border-gold/30" placeholder="Add caption..." autoFocus onKeyDown={e => e.key === "Enter" && saveCaption(m.id)} data-testid={`caption-input-${m.id}`} />
                      <button onClick={() => saveCaption(m.id)} className="text-gold hover:text-gold-light px-1"><Save size={12} /></button>
                      <button onClick={() => setEditingCaption(null)} className="text-white/30 hover:text-white px-1"><X size={12} /></button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingCaption(m.id); setCaptionText(m.caption || ""); }} className="w-full text-left flex items-center gap-1 group/cap" data-testid={`edit-caption-${m.id}`}>
                      <Type size={10} className="text-white/20 group-hover/cap:text-gold/60 shrink-0" />
                      <span className={`text-[10px] font-body truncate ${m.caption ? "text-white/50" : "text-white/20 italic"}`}>{m.caption || "Add caption..."}</span>
                    </button>
                  )}
                </div>
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )}

        {/* Carousel View */}
        {viewMode === "carousel" && images.length > 0 && (
          <div data-testid="gallery-carousel">
            <div className="relative aspect-[16/9] max-h-[70vh] overflow-hidden bg-black">
              <AnimatePresence mode="wait">
                <motion.img key={carouselIdx} src={images[carouselIdx]?.url} alt={images[carouselIdx]?.caption || ""} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }} className="w-full h-full object-contain" />
              </AnimatePresence>
              <button onClick={() => setCarouselIdx(i => (i - 1 + images.length) % images.length)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 border border-white/10 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold/30 transition-all" data-testid="carousel-prev"><ChevronLeft size={20} /></button>
              <button onClick={() => setCarouselIdx(i => (i + 1) % images.length)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 border border-white/10 flex items-center justify-center text-white/60 hover:text-gold hover:border-gold/30 transition-all" data-testid="carousel-next"><ChevronRight size={20} /></button>
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-white font-body text-sm">{images[carouselIdx]?.caption || images[carouselIdx]?.filename}</p>
                <p className="text-white/30 text-[10px] font-body mt-1">{carouselIdx + 1} / {images.length}</p>
              </div>
            </div>
            {/* Thumbnail strip */}
            <div className="flex gap-1 mt-3 overflow-x-auto pb-2">
              {images.map((m, i) => (
                <button key={m.id} onClick={() => setCarouselIdx(i)} className={`shrink-0 w-16 h-16 overflow-hidden border-2 transition-all ${i === carouselIdx ? "border-gold" : "border-transparent opacity-50 hover:opacity-80"}`} data-testid={`thumb-${m.id}`}>
                  <img src={m.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        )}

        {media.length === 0 && !localUploading && (
          <div className="text-center py-24">
            <ImageIcon size={48} className="text-white/10 mx-auto mb-4" />
            <p className="text-white/30 font-body text-sm mb-4">No photos in this gallery yet.</p>
            <button onClick={() => fileRef.current?.click()} className="text-gold text-sm font-body hover:text-gold-light transition-colors" data-testid="empty-upload-btn">Upload your first photos</button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors z-10"><X size={24} /></button>
          {/* Lightbox toolbar — always-visible rotate + smart-reprocess + close */}
          <div onClick={(e) => e.stopPropagation()} className="absolute top-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-2 bg-black/70 border border-white/15 rounded-full">
            <button data-testid="lightbox-rotate-left" onClick={() => rotateMedia(lightbox.id, -90)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white hover:text-gold transition-colors" title="Rotate Left">
              <RotateCcw size={14} /> Rotate Left
            </button>
            <div className="w-px h-4 bg-white/20" />
            <button data-testid="lightbox-rotate-right" onClick={() => rotateMedia(lightbox.id, 90)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white hover:text-gold transition-colors" title="Rotate Right">
              <RotateCw size={14} /> Rotate Right
            </button>
            <div className="w-px h-4 bg-white/20" />
            <button data-testid="lightbox-smart-fix" onClick={() => reprocessSmart(lightbox.id)} disabled={smartReprocess?.loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gold hover:text-white transition-colors disabled:opacity-40" title="Smart Reprocess (AI auto-orient)">
              <Wand2 size={14} /> AI Auto-Orient
            </button>
          </div>
          <img src={lightbox.url} alt={lightbox.caption || ""} className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()} />
          {lightbox.caption && <p className="absolute bottom-8 left-0 right-0 text-center text-white/60 font-body text-sm">{lightbox.caption}</p>}
        </div>
      )}

      {/* Delete Confirmation */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)} data-testid="delete-confirm-modal">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-luxury-charcoal border border-white/10 p-8 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
              <AlertTriangle size={32} className="text-red-400 mx-auto mb-4" />
              <h3 className="text-white font-heading text-xl mb-2">Delete Photo?</h3>
              <p className="text-white/40 font-body text-sm mb-6">This action cannot be undone.</p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => setDeleteConfirm(null)} className="px-6 py-2 border border-white/10 text-white/60 text-sm font-body hover:border-white/20 transition-colors" data-testid="delete-cancel">Cancel</button>
                <button onClick={() => deleteMedia(deleteConfirm)} className="px-6 py-2 bg-red-500/20 text-red-400 text-sm font-body hover:bg-red-500/30 transition-colors" data-testid="delete-confirm">Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Watermark Panel */}
      <AnimatePresence>
        {watermarkPanel && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => setWatermarkPanel(null)} data-testid="watermark-modal">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-luxury-charcoal border border-gold/20 p-8 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <h3 className="text-white font-heading text-xl mb-1">Watermark Settings</h3>
              <p className="text-white/30 font-body text-xs mb-6">Aristocrats JA Signature Events</p>

              <div className="space-y-5">
                <div>
                  <label className="text-white/40 text-[10px] font-body uppercase tracking-wider block mb-2">Position</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[["bottom-left", "Left"], ["bottom-center", "Center"], ["bottom-right", "Right"]].map(([val, label]) => (
                      <button key={val} onClick={() => setWmPos(val)} className={`py-2 text-xs font-body border transition-all ${wmPos === val ? "border-gold text-gold bg-gold/10" : "border-white/10 text-white/40 hover:border-white/20"}`} data-testid={`wm-pos-${val}`}>{label}</button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-white/40 text-[10px] font-body uppercase tracking-wider block mb-2">Opacity ({Math.round(wmOpacity / 255 * 100)}%)</label>
                  <input type="range" min="30" max="255" value={wmOpacity} onChange={e => setWmOpacity(parseInt(e.target.value))} className="w-full accent-gold" data-testid="wm-opacity" />
                </div>

                <div>
                  <label className="text-white/40 text-[10px] font-body uppercase tracking-wider block mb-2">Size ({wmSize}%)</label>
                  <input type="range" min="50" max="200" value={wmSize} onChange={e => setWmSize(parseInt(e.target.value))} className="w-full accent-gold" data-testid="wm-size" />
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button onClick={() => setWatermarkPanel(null)} className="flex-1 py-2 border border-white/10 text-white/60 text-sm font-body hover:border-white/20 transition-colors">Cancel</button>
                <button onClick={applyWatermark} disabled={saving} className="flex-1 py-2 bg-gold text-black text-sm font-body font-medium hover:bg-gold-light transition-colors disabled:opacity-40" data-testid="apply-watermark">{saving ? "Applying..." : "Apply Watermark"}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Move to Position Modal */}
      <AnimatePresence>
        {movePanel && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => { setMovePanel(null); setMoveInput(""); }} data-testid="move-modal">
            <motion.div initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 12 }} className="bg-luxury-charcoal border border-gold/30 max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-1">
                <ArrowUpDown size={18} className="text-gold" />
                <h3 className="text-white font-heading text-lg">Move photo</h3>
              </div>
              <p className="text-white/40 text-xs font-body mb-5">
                Currently at position <span className="text-gold">#{movePanel.current_position}</span> of {media.length}. Move to any position from 1 to {media.length}.
              </p>
              <div className="flex gap-2 mb-4">
                <button data-testid="move-to-first" onClick={() => moveToPosition(movePanel.id, 1)} className="flex-1 py-2.5 px-3 border border-white/15 hover:border-gold/60 text-white/80 hover:text-gold text-xs font-body uppercase tracking-wider transition-colors">
                  Move to first
                </button>
                <button data-testid="move-to-last" onClick={() => moveToPosition(movePanel.id, media.length)} className="flex-1 py-2.5 px-3 border border-white/15 hover:border-gold/60 text-white/80 hover:text-gold text-xs font-body uppercase tracking-wider transition-colors">
                  Move to last
                </button>
              </div>
              <div className="space-y-2">
                <label className="block text-[10px] font-body uppercase tracking-wider text-white/40">Or jump to position</label>
                <div className="flex gap-2">
                  <input
                    data-testid="move-input"
                    type="number"
                    min="1"
                    max={media.length}
                    value={moveInput}
                    onChange={(e) => setMoveInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") moveToPosition(movePanel.id, moveInput); }}
                    autoFocus
                    className="flex-1 bg-white/5 border border-white/10 focus:border-gold/40 px-3 py-2.5 text-sm font-body text-white outline-none transition-colors"
                  />
                  <button
                    data-testid="move-confirm"
                    onClick={() => moveToPosition(movePanel.id, moveInput)}
                    className="px-5 py-2.5 bg-gold/15 hover:bg-gold/25 border border-gold/40 text-gold text-xs font-body uppercase tracking-wider transition-colors"
                  >
                    Move
                  </button>
                </div>
              </div>
              <button data-testid="move-cancel" onClick={() => { setMovePanel(null); setMoveInput(""); }} className="w-full mt-4 py-2 text-white/40 hover:text-white/70 text-xs font-body uppercase tracking-wider transition-colors">
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Smart Reprocess — Before / After Comparison Modal */}
      <AnimatePresence>
        {smartReprocess && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-4" onClick={() => !smartReprocess.loading && setSmartReprocess(null)} data-testid="smart-reprocess-modal">
            <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }} className="bg-luxury-charcoal border border-gold/30 max-w-6xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-white/5">
                <div className="flex items-center gap-3">
                  <Wand2 size={20} className="text-gold" />
                  <div>
                    <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Smart Reprocess</p>
                    <h3 className="text-white font-heading text-lg">{smartReprocess.loading ? "Processing..." : "Before / After"}</h3>
                  </div>
                </div>
                <button onClick={() => !smartReprocess.loading && setSmartReprocess(null)} disabled={smartReprocess.loading} className="text-white/40 hover:text-white transition-colors disabled:opacity-30" data-testid="close-reprocess-modal"><X size={20} /></button>
              </div>

              {smartReprocess.loading ? (
                <div className="py-24 text-center">
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }} className="inline-block mb-4">
                    <Wand2 size={32} className="text-gold" />
                  </motion.div>
                  <p className="text-white/60 font-body text-sm">Running EXIF + visual-AI orientation,</p>
                  <p className="text-white/60 font-body text-sm">white balance, and face-aware watermark...</p>
                </div>
              ) : (
                <>
                  {/* Metadata strip */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 border-b border-white/5">
                    <div className="bg-luxury-charcoal p-4">
                      <p className="text-[10px] font-body uppercase tracking-wider text-white/30 mb-1">Rotation Applied</p>
                      <p className={`font-heading text-lg ${smartReprocess.rotation_applied ? "text-gold" : "text-white/60"}`} data-testid="meta-rotation">
                        {smartReprocess.rotation_applied ? `${smartReprocess.rotation_applied}° CW` : "None needed"}
                      </p>
                    </div>
                    <div className="bg-luxury-charcoal p-4">
                      <p className="text-[10px] font-body uppercase tracking-wider text-white/30 mb-1 flex items-center gap-1.5"><Users size={9} /> Faces Detected</p>
                      <p className="text-white font-heading text-lg" data-testid="meta-faces">{smartReprocess.faces_detected}</p>
                    </div>
                    <div className="bg-luxury-charcoal p-4">
                      <p className="text-[10px] font-body uppercase tracking-wider text-white/30 mb-1 flex items-center gap-1.5"><Droplets size={9} /> Watermark</p>
                      <p className="text-gold font-heading text-lg capitalize" data-testid="meta-watermark">{(smartReprocess.watermark_position || "").replace("-", " ")}</p>
                    </div>
                    <div className="bg-luxury-charcoal p-4">
                      <p className="text-[10px] font-body uppercase tracking-wider text-white/30 mb-1 flex items-center gap-1.5"><Sun size={9} /> Brightness</p>
                      <p className="text-white font-heading text-lg" data-testid="meta-brightness">
                        {smartReprocess.brightness_before} <span className="text-white/30 mx-1">→</span> {smartReprocess.brightness_after}
                      </p>
                    </div>
                  </div>

                  {/* Side-by-side image comparison */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
                    <div className="bg-luxury-charcoal p-4">
                      <p className="text-[10px] font-body uppercase tracking-[0.2em] text-white/40 mb-3 text-center">Before</p>
                      <div className="aspect-[4/3] bg-black flex items-center justify-center overflow-hidden">
                        <img src={smartReprocess.before_url} alt="Before reprocess" className="max-w-full max-h-full object-contain" data-testid="before-image" />
                      </div>
                    </div>
                    <div className="bg-luxury-charcoal p-4">
                      <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold mb-3 text-center">After (Smart Pipeline)</p>
                      <div className="aspect-[4/3] bg-black flex items-center justify-center overflow-hidden">
                        <img src={smartReprocess.after_url} alt="After reprocess" className="max-w-full max-h-full object-contain" data-testid="after-image" />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 p-6 border-t border-white/5">
                    <p className="text-white/30 font-body text-[11px] hidden sm:block">
                      Tip: the &ldquo;Before&rdquo; snapshot is overwritten on the next reprocess. To revert, use Replace Photo.
                    </p>
                    <button onClick={() => setSmartReprocess(null)} className="ml-auto px-6 py-2 bg-gold text-black text-sm font-body font-medium hover:bg-gold-light transition-colors" data-testid="close-reprocess-done">Done</button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Smart Reprocess — Live Progress Modal */}
      <AnimatePresence>
        {bulkReprocess && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-4" onClick={() => bulkReprocess.done && setBulkReprocess(null)} data-testid="bulk-reprocess-modal">
            <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }} className="bg-luxury-charcoal border border-gold/30 max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              {(() => {
                const items = bulkReprocess.items || [];
                const total = items.length;
                const doneCount = items.filter(i => i.status === "done").length;
                const errCount = items.filter(i => i.status === "error").length;
                const processingCount = items.filter(i => i.status === "processing").length;
                const completed = doneCount + errCount;
                const rotatedCount = items.filter(i => i.result?.rotation_applied).length;
                const facesCount = items.reduce((sum, i) => sum + (i.result?.faces_detected || 0), 0);
                const pct = total ? Math.round((completed / total) * 100) : 0;
                return (
                  <>
                    <div className="flex items-center justify-between p-6 border-b border-white/5">
                      <div className="flex items-center gap-3">
                        <Wand2 size={20} className="text-gold" />
                        <div>
                          <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Bulk Smart Reprocess</p>
                          <h3 className="text-white font-heading text-lg" data-testid="bulk-status-text">
                            {bulkReprocess.done ? `Complete — ${doneCount} processed${errCount ? `, ${errCount} failed` : ""}` : `Processing ${completed} of ${total}...`}
                          </h3>
                        </div>
                      </div>
                      <button onClick={() => bulkReprocess.done && setBulkReprocess(null)} disabled={!bulkReprocess.done} className="text-white/40 hover:text-white transition-colors disabled:opacity-30" data-testid="close-bulk-modal"><X size={20} /></button>
                    </div>

                    {/* Progress bar */}
                    <div className="px-6 py-4 border-b border-white/5">
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <motion.div className={`h-full ${errCount && bulkReprocess.done ? "bg-amber-400" : "bg-gold"}`} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4 }} data-testid="bulk-progress-bar" />
                      </div>
                      <div className="grid grid-cols-4 gap-3 mt-4">
                        <div className="text-center">
                          <p className="text-[9px] font-body uppercase tracking-wider text-white/30">Total</p>
                          <p className="text-white font-heading text-xl" data-testid="bulk-total">{total}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] font-body uppercase tracking-wider text-white/30">Done</p>
                          <p className="text-green-400 font-heading text-xl" data-testid="bulk-done">{doneCount}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] font-body uppercase tracking-wider text-white/30">Rotated</p>
                          <p className="text-gold font-heading text-xl" data-testid="bulk-rotated">{rotatedCount}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[9px] font-body uppercase tracking-wider text-white/30">{errCount ? "Errors" : "Faces"}</p>
                          <p className={`font-heading text-xl ${errCount ? "text-red-400" : "text-white/60"}`} data-testid="bulk-errors-or-faces">{errCount || facesCount}</p>
                        </div>
                      </div>
                    </div>

                    {/* Per-photo list */}
                    <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="bulk-items-list">
                      <div className="space-y-2">
                        {items.map((item, idx) => (
                          <div key={item.id} className="flex items-center gap-3 p-2 bg-black/30 border border-white/5" data-testid={`bulk-item-${idx}`}>
                            <div className="w-12 h-12 bg-black overflow-hidden shrink-0">
                              {item.thumb && <img src={item.thumb} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white/70 text-xs font-body truncate" title={item.filename}>{item.filename}</p>
                              {item.status === "done" && item.result && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {item.result.rotation_applied > 0 && <span className="text-[9px] font-body px-1.5 py-0.5 bg-gold/15 text-gold">{item.result.rotation_applied}° CW</span>}
                                  {item.result.faces_detected > 0 && <span className="text-[9px] font-body px-1.5 py-0.5 bg-white/5 text-white/60"><Users size={8} className="inline mr-0.5" />{item.result.faces_detected}</span>}
                                  <span className="text-[9px] font-body px-1.5 py-0.5 bg-white/5 text-white/60 capitalize"><Droplets size={8} className="inline mr-0.5" />{(item.result.watermark_position || "").replace("-", " ")}</span>
                                </div>
                              )}
                              {item.status === "error" && (
                                <p className="text-red-400 text-[10px] font-body mt-0.5">{item.error}</p>
                              )}
                            </div>
                            <div className="shrink-0">
                              {item.status === "pending" && <span className="text-white/20 text-[10px] font-body">Queued</span>}
                              {item.status === "processing" && <Loader2 size={16} className="text-gold animate-spin" />}
                              {item.status === "done" && <CheckCircle2 size={16} className="text-green-400" />}
                              {item.status === "error" && <XCircle size={16} className="text-red-400" />}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 p-4 border-t border-white/5">
                      <p className="text-white/30 font-body text-[11px] hidden sm:block">
                        {bulkReprocess.done ? "Reprocess complete. Selection cleared." : `Concurrency: 2 • In-flight: ${processingCount}`}
                      </p>
                      <button
                        onClick={() => {
                          if (bulkReprocess.done) { setSelected(new Set()); setBulkReprocess(null); }
                          else { bulkReprocess.cancel = true; setBulkReprocess({ ...bulkReprocess }); }
                        }}
                        className={`ml-auto px-6 py-2 text-sm font-body font-medium transition-colors ${bulkReprocess.done ? "bg-gold text-black hover:bg-gold-light" : "border border-white/10 text-white/60 hover:border-white/20"}`}
                        data-testid={bulkReprocess.done ? "close-bulk-done" : "cancel-bulk"}
                      >
                        {bulkReprocess.done ? "Done" : (bulkReprocess.cancel ? "Stopping..." : "Stop Queueing")}
                      </button>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Highlight Reel — generator modal */}
      <AnimatePresence>
        {highlightReel?.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] bg-black/95 flex items-center justify-center p-4" onClick={() => !highlightReel.generating && setHighlightReel(null)} data-testid="highlight-reel-modal">
            <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }} className="bg-luxury-charcoal border border-gold/30 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b border-white/5">
                <div className="flex items-center gap-3">
                  <Film size={20} className="text-gold" />
                  <div>
                    <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">AI Highlight Reel</p>
                    <h3 className="text-white font-heading text-lg">30-second cinematic reel</h3>
                  </div>
                </div>
                <button onClick={() => !highlightReel.generating && setHighlightReel(null)} disabled={highlightReel.generating} className="text-white/40 hover:text-white transition-colors disabled:opacity-30" data-testid="close-reel-modal"><X size={20} /></button>
              </div>

              <div className="p-6 space-y-5">
                {!highlightReel.result ? (
                  <>
                    {/* Setup form */}
                    <div>
                      <label className="text-white/40 text-[10px] font-body uppercase tracking-wider block mb-2 flex items-center gap-1.5"><Music size={10} /> Music Track</label>
                      <select value={highlightReel.selectedMusicId} disabled={highlightReel.generating} onChange={e => setHighlightReel(prev => ({ ...prev, selectedMusicId: e.target.value }))} className="w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 font-body focus:outline-none focus:border-gold/40 disabled:opacity-40" data-testid="reel-music-select">
                        {(highlightReel.musicTracks || []).map(t => (
                          <option key={t.id} value={t.id} className="bg-luxury-charcoal">{t.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-white/40 text-[10px] font-body uppercase tracking-wider block mb-2">Subtitle (optional)</label>
                      <input type="text" value={highlightReel.subtitle} disabled={highlightReel.generating} onChange={e => setHighlightReel(prev => ({ ...prev, subtitle: e.target.value }))} placeholder="e.g. Kingston · 2025" className="w-full bg-black/40 border border-white/10 text-white text-sm px-3 py-2 font-body focus:outline-none focus:border-gold/40 disabled:opacity-40" maxLength={80} data-testid="reel-subtitle" />
                    </div>

                    {/* Score & Auto-Reel checkbox — closes the loop between AI Curation and the video pipeline */}
                    <label className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${highlightReel.autoScore ? "border-gold/40 bg-gold/5" : "border-white/10 bg-black/30 hover:border-white/20"}`} data-testid="reel-auto-score-toggle">
                      <input type="checkbox" checked={!!highlightReel.autoScore} disabled={highlightReel.generating} onChange={e => setHighlightReel(prev => ({ ...prev, autoScore: e.target.checked }))} className="mt-0.5 accent-gold" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Star size={12} className="text-gold" />
                          <span className="text-white text-xs font-body">Run AI Score Gallery first, then build the reel from top scorers</span>
                        </div>
                        <p className="text-white/40 text-[10px] font-body mt-1 leading-relaxed">
                          Re-scores all {images.length} photos using the latest AI quality model and uses the top 5 highest-scored shots in the reel. Adds ~5-10s to total render time.
                        </p>
                      </div>
                    </label>

                    <div className="bg-black/30 border border-white/5 p-4 text-[11px] font-body text-white/50 leading-relaxed">
                      The reel uses the <span className="text-gold">top {Math.min(images.length, 5)} photos</span> from this gallery (AI-scored if scoring has been run, else most recent), applies <span className="text-gold">Ken Burns pan/zoom</span> per photo, and bookends with luxury intro / outro slates. Output: <span className="text-gold">1080×1920 MP4 · 30 seconds · Reels-ready</span>.
                    </div>

                    {highlightReel.generating ? (
                      <div className="py-8 text-center">
                        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }} className="inline-block mb-3">
                          <Film size={32} className="text-gold" />
                        </motion.div>
                        <p className="text-gold font-body text-sm" data-testid="reel-generating-text">
                          {highlightReel.scoring ? "Scoring all photos..." : (highlightReel.autoScore ? "Reel rendering from top scorers..." : "Rendering your cinematic reel...")}
                        </p>
                        <p className="text-white/30 font-body text-[11px] mt-2">
                          {highlightReel.autoScore ? "Score → Top Picks → Ken Burns → Music. Takes ~45-90 seconds." : "This typically takes 30-60 seconds."}
                        </p>
                      </div>
                    ) : (
                      <button onClick={generateHighlightReel} disabled={!highlightReel.selectedMusicId} className="w-full py-3 bg-gold text-black text-sm font-body font-medium hover:bg-gold-light transition-colors disabled:opacity-40" data-testid="generate-reel-btn">
                        {highlightReel.autoScore ? "Score Gallery + Generate Reel" : "Generate Highlight Reel"}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {/* Result preview */}
                    <div className="flex flex-col items-center gap-4">
                      <div className="bg-black w-full max-w-[360px] mx-auto" style={{ aspectRatio: "9 / 16" }}>
                        <video
                          key={highlightReel.result.id}
                          src={highlightReel.result.url}
                          controls
                          className="w-full h-full"
                          data-testid="reel-video-player"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3 w-full">
                        <div className="bg-black/30 border border-white/5 p-3 text-center">
                          <p className="text-[9px] font-body uppercase tracking-wider text-white/30">Music</p>
                          <p className="text-gold font-body text-[11px] mt-1 truncate" data-testid="reel-music-name" title={highlightReel.result.music_name}>{highlightReel.result.music_name}</p>
                        </div>
                        <div className="bg-black/30 border border-white/5 p-3 text-center">
                          <p className="text-[9px] font-body uppercase tracking-wider text-white/30">Photos</p>
                          <p className="text-white font-heading text-base mt-1" data-testid="reel-photos-count">{highlightReel.result.photos_used?.length || 0}</p>
                        </div>
                        <div className="bg-black/30 border border-white/5 p-3 text-center">
                          <p className="text-[9px] font-body uppercase tracking-wider text-white/30">Size</p>
                          <p className="text-white font-heading text-base mt-1" data-testid="reel-size">{((highlightReel.result.size_bytes || 0) / 1024 / 1024).toFixed(1)} MB</p>
                        </div>
                      </div>
                      <div className="flex gap-3 w-full">
                        <a href={highlightReel.result.url} download={`aristocratsja_reel_${highlightReel.result.id?.slice(0, 8)}.mp4`} className="flex-1 py-2.5 bg-gold text-black text-sm font-body font-medium hover:bg-gold-light transition-colors text-center flex items-center justify-center gap-2" data-testid="reel-download-btn">
                          <Download size={14} /> Download MP4
                        </a>
                        <button onClick={() => setHighlightReel(prev => ({ ...prev, result: null }))} className="px-5 py-2.5 border border-white/10 text-white/60 text-sm font-body hover:border-white/20 transition-colors" data-testid="reel-regenerate-btn">
                          Regenerate
                        </button>
                      </div>
                      <p className="text-white/30 font-body text-[10px] text-center">
                        Tip: Save the MP4 and upload directly to Instagram Reels, TikTok, or YouTube Shorts.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============== FLOATING BULK ACTION BAR ============== */}
      {/* Slides up from the bottom whenever ≥1 photo is selected. Stays
          inside the viewport even on long scrolling galleries. */}
      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 30 }}
            data-testid="bulk-action-bar"
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] bg-luxury-charcoal border border-gold/40 px-4 py-3 shadow-2xl shadow-gold/20 flex items-center gap-1 max-w-[96vw] overflow-x-auto"
          >
            <span className="text-gold font-body text-xs uppercase tracking-wider whitespace-nowrap pr-3 border-r border-white/10">
              Selected: <span className="text-white">{selected.size}</span>
            </span>
            <button onClick={() => setCreateAlbum({ ...createAlbum, open: true })} disabled={saving} data-testid="bulk-create-album" title="Create a new album from these photos" className="flex items-center gap-1.5 text-xs font-body px-3 py-1.5 bg-gold/20 text-gold hover:bg-gold/30 transition-colors disabled:opacity-40 whitespace-nowrap">
              <Plus size={12} /> Create Album
            </button>
            <button onClick={() => bulkWatermark("bottom-center", 150, 100)} disabled={saving} data-testid="bulk-watermark" title="Apply default watermark to all" className="flex items-center gap-1.5 text-xs font-body px-3 py-1.5 text-white/70 hover:text-gold hover:bg-white/5 transition-colors disabled:opacity-40 whitespace-nowrap">
              <Droplets size={12} /> Watermark
            </button>
            <button onClick={() => bulkRotate(90)} disabled={saving} data-testid="bulk-rotate" title="Rotate all 90° clockwise" className="flex items-center gap-1.5 text-xs font-body px-3 py-1.5 text-white/70 hover:text-gold hover:bg-white/5 transition-colors disabled:opacity-40 whitespace-nowrap">
              <RotateCw size={12} /> Rotate
            </button>
            <button onClick={bulkTag} disabled={saving} data-testid="bulk-tag" title="Add tags to all" className="flex items-center gap-1.5 text-xs font-body px-3 py-1.5 text-white/70 hover:text-gold hover:bg-white/5 transition-colors disabled:opacity-40 whitespace-nowrap">
              <Tag size={12} /> Tag
            </button>
            <button onClick={bulkCaption} disabled={saving} data-testid="bulk-caption" title="Set the same caption on all" className="flex items-center gap-1.5 text-xs font-body px-3 py-1.5 text-white/70 hover:text-gold hover:bg-white/5 transition-colors disabled:opacity-40 whitespace-nowrap">
              <FileText size={12} /> Caption
            </button>
            <button onClick={bulkExport} disabled={saving} data-testid="bulk-export" title="Export URLs as manifest" className="flex items-center gap-1.5 text-xs font-body px-3 py-1.5 text-white/70 hover:text-gold hover:bg-white/5 transition-colors disabled:opacity-40 whitespace-nowrap">
              <Download size={12} /> Export
            </button>
            <button onClick={reprocessSelected} disabled={saving || (bulkReprocess && !bulkReprocess.done)} data-testid="bulk-reprocess" title="AI smart-reprocess all" className="flex items-center gap-1.5 text-xs font-body px-3 py-1.5 text-white/70 hover:text-purple-300 hover:bg-purple-500/10 transition-colors disabled:opacity-40 whitespace-nowrap">
              <Sparkles size={12} /> Reprocess
            </button>
            <button onClick={deleteSelected} disabled={saving} data-testid="bulk-delete" title="Delete all selected (with confirmation)" className="flex items-center gap-1.5 text-xs font-body px-3 py-1.5 text-red-300 hover:text-red-200 hover:bg-red-500/15 transition-colors disabled:opacity-40 whitespace-nowrap">
              <Trash2 size={12} /> Delete
            </button>
            <button onClick={deselectAll} disabled={saving} data-testid="bulk-clear" className="text-white/40 hover:text-white px-2 py-1.5 disabled:opacity-40">
              <X size={14} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ============== CREATE ALBUM FROM SELECTION ============== */}
      <AnimatePresence>
        {createAlbum.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCreateAlbum({ ...createAlbum, open: false })}>
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} className="w-full max-w-md bg-luxury-charcoal border border-gold/20 p-6" onClick={(e) => e.stopPropagation()} data-testid="create-album-modal">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold mb-1">From {selected.size} selected photos</p>
                  <h3 className="text-white font-heading text-xl">New album</h3>
                </div>
                <button onClick={() => setCreateAlbum({ ...createAlbum, open: false })} className="text-white/40"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                <input data-testid="create-album-title" value={createAlbum.title} onChange={(e) => setCreateAlbum({ ...createAlbum, title: e.target.value })} placeholder="Album title *" className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none" autoFocus />
                <select data-testid="create-album-type" value={createAlbum.event_type} onChange={(e) => setCreateAlbum({ ...createAlbum, event_type: e.target.value })} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body focus:border-gold/50 outline-none">
                  {["Portfolio", "Wedding", "Corporate", "Yacht & Sea", "Nightlife", "Concert", "Festival", "Private Party", "Product Launch", "Charity Gala", "General"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <input data-testid="create-album-date" type="date" value={createAlbum.event_date} onChange={(e) => setCreateAlbum({ ...createAlbum, event_date: e.target.value })} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body focus:border-gold/50 outline-none" />
                <textarea data-testid="create-album-desc" value={createAlbum.description} onChange={(e) => setCreateAlbum({ ...createAlbum, description: e.target.value })} placeholder="Description (optional)" rows={2} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none resize-none" />
                {/* Copy vs Move toggle */}
                <div className="flex items-center justify-between bg-luxury-black/50 border border-white/5 p-3">
                  <div>
                    <p className="text-white text-xs font-body">Action</p>
                    <p className="text-white/40 text-[10px] font-body mt-0.5">Copy keeps photos in their current album. Move detaches them.</p>
                  </div>
                  <div className="flex border border-white/10">
                    <button onClick={() => setCreateAlbum({ ...createAlbum, action: "copy" })} data-testid="create-album-action-copy" className={`px-3 py-1.5 text-[11px] font-body uppercase ${createAlbum.action === "copy" ? "bg-gold/20 text-gold" : "text-white/40 hover:text-white"}`}>Copy</button>
                    <button onClick={() => setCreateAlbum({ ...createAlbum, action: "move" })} data-testid="create-album-action-move" className={`px-3 py-1.5 text-[11px] font-body uppercase ${createAlbum.action === "move" ? "bg-gold/20 text-gold" : "text-white/40 hover:text-white"}`}>Move</button>
                  </div>
                </div>
                {/* Draft toggle */}
                <label className="flex items-center gap-2 text-white/60 text-xs font-body cursor-pointer">
                  <input type="checkbox" data-testid="create-album-draft" checked={createAlbum.draft} onChange={(e) => setCreateAlbum({ ...createAlbum, draft: e.target.checked })} />
                  Save as draft (hidden from public gallery list)
                </label>
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={() => setCreateAlbum({ ...createAlbum, open: false })} className="flex-1 border border-white/10 text-white/60 hover:text-white px-4 py-2.5 font-body text-xs uppercase tracking-wider transition-colors">Cancel</button>
                <button onClick={submitCreateAlbum} disabled={saving || !createAlbum.title.trim()} data-testid="create-album-submit" className="flex-1 bg-gold hover:bg-gold/90 text-luxury-black px-4 py-2.5 font-body text-xs uppercase tracking-wider font-medium transition-colors disabled:opacity-50">
                  {saving ? "Creating..." : (createAlbum.draft ? "Save draft" : "Publish")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Social Share Dialog — opens on click of the gold Share button.
          Generates 5 platform-ready variants with ARIA captions in the
          background (response is instant, captions stream in via poll). */}
      {shareDialog && (
        <SocialShareDialog
          mediaId={shareDialog.id}
          mediaUrl={shareDialog.url}
          onClose={() => setShareDialog(null)}
        />
      )}
    </div>
  );
}
